/**
 * Comprehensive Permission Management System
 * 
 * This module provides a centralized permission framework for the SHARP web client.
 * It defines all available permissions, permission checking utilities, and context-aware
 * permission management.
 */

// Permission structure: resource:action
export type PermissionString = string;

/**
 * All available permissions in the system
 * These should match the permissions defined in the backend RBAC system
 */
export const PERMISSIONS = {
  // Content permissions
  CONTENT_CREATE: 'content:create',
  CONTENT_READ: 'content:read',
  CONTENT_UPDATE: 'content:update',
  CONTENT_DELETE: 'content:delete',
  CONTENT_PUBLISH: 'content:publish',
  CONTENT_APPROVE: 'content:approve',
  CONTENT_SHARE: 'content:share',
  CONTENT_DOWNLOAD: 'content:download',
  CONTENT_SEARCH: 'content:search',
  CONTENT_COMMENT: 'content:comment',
  CONTENT_SUBMIT: 'content:submit',
  CONTENT_MANAGE: 'content:manage',
  CONTENT_METADATA_MANAGE: 'content:metadata:manage',

  // Organization permissions
  ORGANIZATION_CREATE: 'organization:create',
  ORGANIZATION_READ: 'organization:read',
  ORGANIZATION_UPDATE: 'organization:update',
  ORGANIZATION_DELETE: 'organization:delete',
  ORGANIZATION_SETTINGS: 'organization:settings',

  // Team permissions
  TEAM_CREATE: 'team:create',
  TEAM_READ: 'team:read',
  TEAM_UPDATE: 'team:update',
  TEAM_DELETE: 'team:delete',
  TEAM_SETTINGS: 'team:settings',
  TEAM_INVITE: 'team:invite',

  // Member permissions
  MEMBER_READ: 'member:read',
  MEMBER_INVITE: 'member:invite',
  MEMBER_REMOVE: 'member:remove',

  // Role permissions
  ROLE_READ: 'role:read',
  ROLE_ASSIGN: 'role:assign',
  ROLE_REVOKE: 'role:revoke',

  // System permissions
  SYSTEM_ADMIN: 'system:admin',
  SYSTEM_MANAGE: 'system:manage',
  SYSTEM_READ: 'system:read',

  // Profile permissions
  PROFILE_READ: 'profile:read',
  PROFILE_UPDATE: 'profile:update',

  // Dashboard permissions
  DASHBOARD_READ: 'dashboard:read',

  // Invitation permissions
  INVITATION_READ: 'invitation:read',
  INVITATION_CANCEL: 'invitation:cancel',
  INVITATION_RESEND: 'invitation:resend',
  INVITATION_ACCEPT: 'invitation:accept',
  INVITATION_DECLINE: 'invitation:decline',

  // Subscription permissions (billing/financial operations)
  SUBSCRIPTION_CREATE: 'subscription:create',
  SUBSCRIPTION_READ: 'subscription:read',
  SUBSCRIPTION_UPDATE: 'subscription:update',
  SUBSCRIPTION_CANCEL: 'subscription:cancel',
  SUBSCRIPTION_REACTIVATE: 'subscription:reactivate',
  SUBSCRIPTION_DELETE: 'subscription:delete',
  SUBSCRIPTION_ASSIGN_TEAM: 'subscription:assign-team',
  SUBSCRIPTION_VIEW_ACTIVE_SESSIONS: 'subscription:view-active-sessions',
  SUBSCRIPTION_FORCE_DEACTIVATE: 'subscription:force-deactivate',
  SUBSCRIPTION_VIEW_METRICS: 'subscription:view-metrics',

  // Payment Method permissions (billing/financial operations)
  PAYMENT_CREATE: 'payment:create',
  PAYMENT_READ: 'payment:read',
  PAYMENT_UPDATE: 'payment:update',
  PAYMENT_DELETE: 'payment:delete',

  // Analytics permissions
  ANALYTICS_READ: 'analytics:read',
  ANALYTICS_EXPORT: 'analytics:export',

  // View permissions — consolidated under content:* (Plan A views-as-content unification).
  // The VIEW_* names are kept for caller readability; views are content rows with kind='view'.
  VIEW_CREATE: 'content:create',
  VIEW_READ: 'content:read',
  VIEW_UPDATE: 'content:edit-payload',
  VIEW_DELETE: 'content:delete',
  VIEW_SHARE: 'content:share',
} as const;

/**
 * Permission groups for common UI scenarios
 */
export const PERMISSION_GROUPS = {
  // Organization management
  ORGANIZATION_ADMIN: [
    PERMISSIONS.ORGANIZATION_READ,
    PERMISSIONS.ORGANIZATION_UPDATE,
    PERMISSIONS.ORGANIZATION_DELETE,
    PERMISSIONS.ORGANIZATION_SETTINGS,
    PERMISSIONS.MEMBER_INVITE,
    PERMISSIONS.MEMBER_REMOVE,
    PERMISSIONS.TEAM_CREATE,
    PERMISSIONS.TEAM_SETTINGS,
    PERMISSIONS.ROLE_ASSIGN,
    PERMISSIONS.ROLE_REVOKE,
    PERMISSIONS.INVITATION_READ,
    PERMISSIONS.INVITATION_CANCEL,
    PERMISSIONS.SUBSCRIPTION_UPDATE,
  ],

  // Team management
  TEAM_ADMIN: [
    PERMISSIONS.TEAM_READ,
    PERMISSIONS.TEAM_UPDATE,
    PERMISSIONS.TEAM_SETTINGS,
    PERMISSIONS.MEMBER_INVITE,
    PERMISSIONS.MEMBER_READ,
    PERMISSIONS.CONTENT_MANAGE,
  ],

  // Content management
  CONTENT_MANAGER: [
    PERMISSIONS.CONTENT_CREATE,
    PERMISSIONS.CONTENT_READ,
    PERMISSIONS.CONTENT_UPDATE,
    PERMISSIONS.CONTENT_DELETE,
    PERMISSIONS.CONTENT_PUBLISH,
    PERMISSIONS.CONTENT_APPROVE,
  ],

  // Basic member permissions
  MEMBER: [
    PERMISSIONS.CONTENT_READ,
    PERMISSIONS.CONTENT_COMMENT,
    PERMISSIONS.CONTENT_SHARE,
  ],

  // Invitation management
  INVITATION_MANAGER: [
    PERMISSIONS.INVITATION_READ,
    PERMISSIONS.INVITATION_CANCEL,
    PERMISSIONS.INVITATION_RESEND,
    PERMISSIONS.MEMBER_INVITE,
  ],
};

/**
 * Context type for permission checking
 */
export interface PermissionContext {
  organizationId?: string;
  teamId?: string;
}

/**
 * Aggregated permission state
 */
export interface PermissionState {
  permissions: Set<PermissionString>;
  context: PermissionContext;
  lastUpdated: Date;
}

/**
 * Utility functions for permission checking
 */
export class PermissionChecker {
  private permissions: Set<PermissionString>;

  constructor(permissions: PermissionString[]) {
    this.permissions = new Set(permissions);
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: PermissionString): boolean {
    return this.permissions.has(permission);
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: PermissionString[]): boolean {
    return permissions.every(permission => this.permissions.has(permission));
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: PermissionString[]): boolean {
    return permissions.some(permission => this.permissions.has(permission));
  }

  /**
   * Check if user has all permissions in a permission group
   */
  hasPermissionGroup(group: PermissionString[]): boolean {
    return this.hasAllPermissions(group);
  }

  /**
   * Check if user has any permissions in a permission group
   */
  hasAnyPermissionInGroup(group: PermissionString[]): boolean {
    return this.hasAnyPermission(group);
  }

  /**
   * Get all permissions as array
   */
  getAllPermissions(): PermissionString[] {
    return Array.from(this.permissions);
  }

  /**
   * Get permissions for a specific resource
   */
  getResourcePermissions(resource: string): PermissionString[] {
    return this.getAllPermissions().filter(permission => 
      permission.startsWith(`${resource}:`)
    );
  }
}

/**
 * Common permission checking utilities for UI components
 */
export const PermissionUtils = {
  /**
   * Can user view organization invitations?
   */
  canViewInvitations: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.INVITATION_READ,
      PERMISSIONS.MEMBER_INVITE,
      PERMISSIONS.ORGANIZATION_SETTINGS,
    ]);
  },

  /**
   * Can user invite members?
   */
  canInviteMembers: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.MEMBER_INVITE,
      PERMISSIONS.ORGANIZATION_SETTINGS,
      PERMISSIONS.TEAM_SETTINGS,
    ]);
  },

  /**
   * Can user manage teams?
   */
  canManageTeams: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.TEAM_CREATE,
      PERMISSIONS.TEAM_UPDATE,
      PERMISSIONS.TEAM_DELETE,
      PERMISSIONS.TEAM_SETTINGS,
      PERMISSIONS.ORGANIZATION_SETTINGS,
    ]);
  },

  /**
   * Can user manage organization?
   */
  canManageOrganization: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.ORGANIZATION_SETTINGS,
      PERMISSIONS.ORGANIZATION_UPDATE,
    ]);
  },

  /**
   * Can user manage roles?
   */
  canManageRoles: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.ROLE_ASSIGN,
      PERMISSIONS.ROLE_REVOKE,
      PERMISSIONS.ORGANIZATION_SETTINGS,
    ]);
  },

  /**
   * Can user view subscriptions?
   */
  canViewSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_READ);
  },

  /**
   * Can user create subscriptions?
   * OrgAdmin only - billing/financial operation
   */
  canCreateSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_CREATE);
  },

  /**
   * Can user update subscriptions (modify settings, add-ons, reference name)?
   * OrgAdmin only - billing/financial operation
   */
  canUpdateSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_UPDATE);
  },

  /**
   * Can user cancel subscriptions?
   * OrgAdmin only - billing/financial operation
   */
  canCancelSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_CANCEL);
  },

  /**
   * Can user reactivate subscriptions?
   * OrgAdmin only - billing/financial operation
   */
  canReactivateSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_REACTIVATE);
  },

  /**
   * Can user delete subscriptions?
   * OrgAdmin only - billing/financial operation
   */
  canDeleteSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_DELETE);
  },

  /**
   * Can user assign teams to subscriptions?
   * OrgAdmin and OrgMember both have this permission
   */
  canAssignTeamsToSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_ASSIGN_TEAM);
  },

  /**
   * Can user manage subscription add-ons?
   * OrgAdmin only - billing/financial operation
   */
  canManageSubscriptionAddOns: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.SUBSCRIPTION_UPDATE);
  },

  /**
   * Can user manage ANY subscription operations (create, update, cancel, delete)?
   * OrgAdmin only - any billing/financial operations
   */
  canManageSubscriptions: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.SUBSCRIPTION_CREATE,
      PERMISSIONS.SUBSCRIPTION_UPDATE,
      PERMISSIONS.SUBSCRIPTION_CANCEL,
      PERMISSIONS.SUBSCRIPTION_REACTIVATE,
      PERMISSIONS.SUBSCRIPTION_DELETE,
    ]);
  },

  /**
   * Can user view payment methods?
   * OrgAdmin only - sensitive billing information
   */
  canViewPaymentMethods: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.PAYMENT_READ);
  },

  /**
   * Can user add payment methods?
   * OrgAdmin only - billing/financial operation
   */
  canAddPaymentMethods: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.PAYMENT_CREATE);
  },

  /**
   * Can user update payment methods (set default)?
   * OrgAdmin only - billing/financial operation
   */
  canUpdatePaymentMethods: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.PAYMENT_UPDATE);
  },

  /**
   * Can user delete payment methods?
   * OrgAdmin only - billing/financial operation
   */
  canDeletePaymentMethods: (checker: PermissionChecker): boolean => {
    return checker.hasPermission(PERMISSIONS.PAYMENT_DELETE);
  },

  /**
   * Can user manage ANY payment method operations?
   * OrgAdmin only - any billing/financial operations
   */
  canManagePaymentMethods: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.PAYMENT_CREATE,
      PERMISSIONS.PAYMENT_READ,
      PERMISSIONS.PAYMENT_UPDATE,
      PERMISSIONS.PAYMENT_DELETE,
    ]);
  },

  /**
   * Can user view organization settings?
   */
  canViewOrganizationSettings: (checker: PermissionChecker): boolean => {
    return checker.hasAnyPermission([
      PERMISSIONS.ORGANIZATION_READ,
      PERMISSIONS.ORGANIZATION_SETTINGS,
    ]);
  },
};

/**
 * Permission debugging utilities
 */
export const PermissionDebug = {
  /**
   * Log current permissions for debugging
   */
  logPermissions: (permissions: PermissionString[], context?: PermissionContext): void => {
    console.group('🔒 Current User Permissions');
    console.log('Context:', context);
    console.log('Permissions:', permissions.sort());
    console.groupEnd();
  },

  /**
   * Check specific permission and log result
   */
  debugPermissionCheck: (
    checker: PermissionChecker, 
    permission: PermissionString,
    description?: string
  ): boolean => {
    const hasPermission = checker.hasPermission(permission);
    console.log(
      `🔒 Permission Check: ${permission} ${hasPermission ? '✅' : '❌'}`,
      description ? `(${description})` : ''
    );
    return hasPermission;
  },
};